import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FiShoppingBag, FiPackage } from "react-icons/fi";
import { MdCompare } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import AppLoader from "../../Components/Common/AppLoader";
import Pagination from "../../Components/Common/Pagination";
import { useAuth } from "../../Components/Context/AuthContext";
import BulkProformaInvoiceModal from "../../Components/UserSideComponents/Quotations/BulkProformaInvoiceModal";
import ComparisonModal from "../../Components/UserSideComponents/Quotations/ComparisonModal";
import OrderListItem from "../../Components/UserSideComponents/Quotations/OrderListItem";
import QuotationCard from "../../Components/UserSideComponents/Quotations/QuotationCard";
import QuotationDetailsContent from "../../Components/UserSideComponents/Quotations/QuotationDetailsContent";
import QuotationSummaryCard from "../../Components/UserSideComponents/Quotations/QuotationSummaryCard";
import SampleCartBar from "../../Components/UserSideComponents/Quotations/SampleCartBar";
import SampleRequestConfirmationModal from "../../Components/UserSideComponents/Quotations/SampleRequestConfirmationModal";
import SampleRequestModal from "../../Components/UserSideComponents/Quotations/SampleRequestModal";
import SelectedQuotationView from "../../Components/UserSideComponents/Quotations/SelectedQuotationView";
import SelectedQuotationsListView from "../../Components/UserSideComponents/Quotations/SelectedQuotationsListView";
import axios from "../../Utils/axiosInstance";
import { assets } from "./../../Assets/assets";
import { useLockBodyScroll } from "./../../Components/Hooks/useLockBodyScroll";
import TransactionsHistoryTab from "../../Components/UserSideComponents/Orders/TransactionsHistoryTab";

// ─── Skeleton ─────────────────────────────────────────
const OrderSkeleton = () => (
  <div className="p-4 rounded-lg border border-gray-200 animate-pulse">
    <div className="h-4 w-32 bg-gray-200 rounded mb-3" />
    <div className="h-3 w-24 bg-gray-200 rounded mb-2" />
    <div className="h-3 w-20 bg-gray-200 rounded" />
  </div>
);

// ─── Empty State ──────────────────────────────────────
const EmptyOrders = () => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <img
      src={assets.noordervector}
      alt="No Orders"
      className="w-64 md:w-80 mb-5 opacity-90"
    />
    <h2 className="text-xl font-semibold text-gray-600">
      No Orders with Quotations Found
    </h2>
    <p className="text-sm text-gray-400 mt-1">
      Orders with quotations will appear here.
    </p>
  </div>
);

const EmptyQuotationState = () => (
  <div className="flex flex-col items-center justify-center py-16 text-center px-4">
    <div className="w-16 h-16 rounded-full bg-[#772037]/10 flex items-center justify-center mb-4">
      <span className="text-2xl">📄</span>
    </div>
    <h2 className="text-lg font-semibold text-gray-800 mb-1">
      Quotations will appear here
    </h2>
    <p className="text-sm text-gray-500 mb-8 max-w-sm">
      Your order follows a secure process before quotations are shown
    </p>
    <div className="flex flex-col items-center">
      <div className="px-4 py-2 rounded-lg bg-green-50 text-green-700 text-xs font-semibold border border-green-200 shadow-sm">
        Order Placed
      </div>
      <div className="text-gray-400 text-lg">↓</div>
      <div className="px-4 py-2 rounded-lg bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-200 shadow-sm">
        Quotations Submitted
      </div>
      <div className="text-gray-400 text-lg">↓</div>
      <div className="px-4 py-2 rounded-lg bg-purple-50 text-purple-700 text-xs font-semibold border border-purple-200 shadow-sm">
        Admin Verifies & Validates
      </div>
      <div className="text-gray-400 text-lg">↓</div>
      <div className="px-4 py-2 rounded-lg bg-[#772037]/10 text-[#772037] text-xs font-semibold border border-[#772037]/20 shadow-sm">
        Quotations Appear Here
      </div>
    </div>
    <p className="text-xs text-gray-400 mt-8">
      You'll be notified once quotations are ready
    </p>
  </div>
);

// ─── Error State ──────────────────────────────────────
const ErrorState = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <p className="text-red-500 font-medium mb-3">Failed to load orders</p>
    <button
      onClick={onRetry}
      className="px-4 py-2 bg-primaryColor text-white rounded-lg text-sm"
    >
      Retry
    </button>
  </div>
);

// ─── Match color helper ───────────────────────────────
export const getMatchMeta = (pct) => {
  if (pct >= 80)
    return {
      bg: "bg-green-50",
      border: "border-green-200",
      text: "text-green-700",
      bar: "bg-green-500",
      label: "Excellent",
    };
  if (pct >= 60)
    return {
      bg: "bg-blue-50",
      border: "border-blue-200",
      text: "text-blue-700",
      bar: "bg-blue-500",
      label: "Good",
    };
  if (pct >= 40)
    return {
      bg: "bg-yellow-50",
      border: "border-yellow-200",
      text: "text-yellow-700",
      bar: "bg-yellow-500",
      label: "Fair",
    };
  return {
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
    bar: "bg-red-400",
    label: "Low",
  };
};

// ─── Main Component ───────────────────────────────────
const Quotations = () => {
  const [orders, setOrders] = useState([]);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [error, setError] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [quotationCount, setQuotationCount] = useState(0);
  const [loadingQuotations, setLoadingQuotations] = useState(false);

  // ── confirmation modal for adding to cart (sample vs full order) ──────────────
  const [showSampleConfirmationModal, setShowSampleConfirmationModal] = useState(false);

  // ── single selected quotation (direct full order, unchanged) ──────────────
  const [selectedQuotation, setSelectedQuotation] = useState(null);

  // ── multi sample quotations ───────────────────────────────────────────────
  const [selectedQuotations, setSelectedQuotations] = useState([]);

  // ── drill-down from list into SelectedQuotationView ───────────────────────
  const [activeSelectedQuotation, setActiveSelectedQuotation] = useState(null);

  // ── sample cart ───────────────────────────────────────────────────────────
  const [sampleCart, setSampleCart] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);
  const [cartProceedLoading, setCartProceedLoading] = useState(false);

  // ── per-card cart loading (track which quotationId is being toggled) ───────
  const [cardCartLoadingId, setCardCartLoadingId] = useState(null);

  // ── full order proceed loading (from list view) ───────────────────────────
  const [fullOrderLoadingId, setFullOrderLoadingId] = useState(null);
  const [fullOrderUpdating, setFullOrderUpdating] = useState(false);

  // ── compare + quotation details ───────────────────────────────────────────
  const [selectedQuotationDetails, setSelectedQuotationDetails] = useState(null);
  const [checkedQuotations, setCheckedQuotations] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);

  // ── sample request modal ──────────────────────────────────────────────────
  const [showSampleModal, setShowSampleModal] = useState(false);
  const [pendingCartQuotation, setPendingCartQuotation] = useState(null);

  // ── view mode: "grid" | "list" ────────────────────────────────────────────
  const [showGridForAddMore, setShowGridForAddMore] = useState(false);

  // Bulk Proforma Invoice Modal ──────────────────────────────────────
  const [bulkProformaModalOpen, setBulkProformaModalOpen] = useState(false);

  // Track unpaid quotations for payment modal ────────────────────────
  const [unpaidQuotationsForPayment, setUnpaidQuotationsForPayment] = useState([]);

  // Transactions view toggle
  const [showTransactions, setShowTransactions] = useState(false);

  // Toggle: show sample list alongside full order view
  const [showSampleListAlongside, setShowSampleListAlongside] = useState(false);

  const lockScroll = showCompareModal || showSampleModal || bulkProformaModalOpen || showSampleConfirmationModal;
  // Hook to lock body scroll when modals are open
  useLockBodyScroll(lockScroll);

  // ── Derived: cart item IDs set ────────────────────────────────────────────
  const cartQuotationIds = useMemo(() => {
    if (!sampleCart?.items) return new Set();
    return new Set(
      sampleCart.items.map((item) =>
        typeof item.quotationId === "object"
          ? item.quotationId._id?.toString()
          : item.quotationId?.toString()
      )
    );
  }, [sampleCart]);

  // DATA FETCHING
  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(false);
      const { data } = await axios.get(
        `/order/get-orders-created-by-user?page=${page}&limit=${limit}&quotationOnly=true`
      );
      const ordersWithQuotations = data.data?.orders || data.data || [];
      setOrders(ordersWithQuotations);
      setPagination(data.data?.pagination || null);

      if (selectedOrder) {
        const updated = ordersWithQuotations.find(
          (o) => o._id === selectedOrder._id
        );
        if (updated) setSelectedOrder(updated);
      }
      if (
        !location.search &&
        ordersWithQuotations.length > 0 &&
        window.innerWidth >= 768
      ) {
        setSelectedOrder(ordersWithQuotations[0]);
      }
    } catch {
      setError(true);
      toast.error("Failed to fetch orders");
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotations = async (orderId) => {
    try {
      setLoadingQuotations(true);
      const { data } = await axios.get(
        `/quotation/get-quotation-matchings-by-order/${orderId}`
      );
      const allQuotations = data.data || [];

      // Sample-selected: quotations that were part of the sample flow
      // Include BOTH:
      // 1. Still in sample stage (Proceed_With_Sample)
      // 2. Already moved to full order after sample approval (Proceed_Full_Order with sample requested)
      const sampleSelected = allQuotations.filter(
        (q) =>
          q.buyerDecision === "Proceed_With_Sample" ||
          (
            q.buyerDecision === "Proceed_Full_Order" &&
            q.sampleDetails?.requested === true
          )
      );

      // FIXED: Full order selected includes BOTH:
      // 1. Direct full order (no sample involved)
      // 2. Full order after approved sample
      const fullOrderSelected = allQuotations.find(
        (q) =>
          q.isSelectedByBuyer &&
          q.buyerDecision === "Proceed_Full_Order" &&
          // Accept quotations with:
          // - NO sample details (direct full order), OR
          // - Approved sample details (sample → full order)
          (
            !q.sampleDetails?.requested ||
            (q.sampleDetails?.requested && q.sampleDetails?.approval === "Approved")
          )
      );

      setSelectedQuotations(sampleSelected);
      setSelectedQuotation(fullOrderSelected || null);
      setQuotations(allQuotations);
      setQuotationCount(allQuotations.length);
    } catch {
      toast.error("Failed to fetch quotations");
      setQuotations([]);
      setSelectedQuotations([]);
      setSelectedQuotation(null);
    } finally {
      setLoadingQuotations(false);
    }
  };

  const fetchSampleCart = async (orderId) => {
    try {
      const { data } = await axios.get(`/sample-cart/order/${orderId}`);
      if (data?.success) setSampleCart(data.data);
      else setSampleCart(null);
    } catch {
      // 404 means no active cart — not an error
      setSampleCart(null);
    }
  };

  // useEffects
  useEffect(() => {
    fetchOrders();
  }, [page, limit]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const highlightId = params.get("highlight");
    if (highlightId && orders.length) {
      const foundOrder = orders.find((o) => o._id === highlightId);
      if (foundOrder) setSelectedOrder(foundOrder);
    }
  }, [location.search, orders]);

  useEffect(() => {
    if (selectedOrder) {
      fetchQuotations(selectedOrder._id);
      fetchSampleCart(selectedOrder._id);
      setCheckedQuotations([]);
      setActiveSelectedQuotation(null);
      setSelectedQuotationDetails(null);
      setShowGridForAddMore(false);
      setShowTransactions(false);
      setShowSampleListAlongside(false);
    }
  }, [selectedOrder]);

  // useEffect(() => {
  //   if (activeSelectedQuotation && quotations.length > 0) {
  //     const updated = quotations.find(q => q._id === activeSelectedQuotation._id);
  //     if (updated) setActiveSelectedQuotation(updated);
  //   }
  // }, [quotations]);

  useEffect(() => {
    if (activeSelectedQuotation && quotations.length > 0) {
      const updated = quotations.find(q => q._id === activeSelectedQuotation._id);
      if (updated && JSON.stringify(updated) !== JSON.stringify(activeSelectedQuotation)) {
        setActiveSelectedQuotation(updated);
      }
    }
  }, [quotations]);

  // useEffect(() => {
  //   if (activeSelectedQuotation) return; // already in drill-down, don't override

  //   const approvedPending = selectedQuotations.find(
  //     (q) =>
  //       q.sampleDetails?.approval === "Approved" &&
  //       q.buyerDecision === "Proceed_With_Sample"
  //   );

  //   if (approvedPending) {
  //     setActiveSelectedQuotation(approvedPending);
  //   }
  // }, [selectedQuotations]);

  // If the quotation currently being drilled into has itself fully
  // committed to the full order, always fall back to the no-back-button
  // "direct full order" view instead of staying in drill-down mode.
  useEffect(() => {
    if (
      activeSelectedQuotation?.isSelectedByBuyer &&
      activeSelectedQuotation?.buyerDecision === "Proceed_Full_Order" &&
      (!activeSelectedQuotation?.sampleDetails?.requested ||
        activeSelectedQuotation?.sampleDetails?.approval === "Approved")
    ) {
      setActiveSelectedQuotation(null);
    }
  }, [activeSelectedQuotation]);

  // CART ACTIONS
  const handleAddToCart = (quotation) => {
    // If cart is already active (sample mode), add directly without modal
    if (sampleCart && sampleCart.items?.length > 0) {
      doAddToCart(quotation);
      return;
    }

    // First addition — ask sample or full order
    setPendingCartQuotation(quotation);
    setShowSampleModal(true);
  };

  const doAddToCart = async (quotation) => {
    try {
      setCardCartLoadingId(quotation._id);
      const { data } = await axios.post("/sample-cart/add", {
        orderId: selectedOrder._id,
        quotationIds: [quotation._id],
      });
      if (data?.success) {
        toast.success("Added to sample cart");
        await fetchSampleCart(selectedOrder._id);
      } else {
        toast.error(data?.message || "Failed to add to cart");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add to cart");
    } finally {
      setCardCartLoadingId(null);
    }
  };

  const handleRemoveFromCart = async (quotation) => {
    try {
      setCardCartLoadingId(quotation._id);
      const { data } = await axios.delete("/sample-cart/remove", {
        data: { orderId: selectedOrder._id, quotationIds: [quotation._id] },
      });
      if (data?.success) {
        toast.success("Removed from cart");
        await fetchSampleCart(selectedOrder._id);
      } else {
        toast.error(data?.message || "Failed to remove");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to remove");
    } finally {
      setCardCartLoadingId(null);
    }
  };

  const handleSampleModalConfirm = async (isSampleRequested) => {
    if (!pendingCartQuotation) return;

    setShowSampleModal(false);

    if (isSampleRequested) {
      // Add to sample cart
      await doAddToCart(pendingCartQuotation);
      setPendingCartQuotation(null);
    } else {
      // Direct full order — existing flow
      await handleDirectFullOrder(pendingCartQuotation);
      setPendingCartQuotation(null);
    }
  };

  const handleDirectFullOrder = async (quotation) => {
    try {
      const loadingToast = toast.loading("Processing...");
      const { data } = await axios.post(
        `/transport-charge/${quotation._id}/express-interest`,
        { charge_is_for: "Full_Order", isSampleRequested: false }
      );
      toast.dismiss(loadingToast);
      if (data?.success) {
        toast.success("Interest registered successfully!");
        await fetchOrders();
        if (selectedOrder) await fetchQuotations(selectedOrder._id);
      } else {
        toast.error(data?.message || "Failed to express interest");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to process"
      );
    }
  };

  const handleAddMoreFromConfirmation = () => {
    setShowGridForAddMore(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSwitchToFullOrderFromConfirmation = async () => {
    try {
      // Use direct delete API instead of removing items one by one
      if (sampleCart?._id) {
        const { data } = await axios.delete(
          `/sample-cart/${sampleCart._id}/delete`
        );

        if (data?.success) {
          toast.success("Sample cart cleared");
          await fetchSampleCart(selectedOrder._id);
        } else {
          toast.error(data?.message || "Failed to clear cart");
          return;
        }
      }

      // Reset UI
      setShowGridForAddMore(false);
      setCheckedQuotations([]);

      // User will now select a quotation and can choose full order
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to clear cart");
    }
  };

  const handleProceedSamplePayment = async (quotations) => {
    if (!quotations || quotations.length === 0) {
      toast.error("No quotations selected");
      return;
    }

    // FILTER: Only include unpaid quotations
    const unpaidQuotations = quotations.filter((q) => {
      const isAlreadyPaid = sampleCart?.items?.some(
        (item) =>
          item.quotationId?._id?.toString() === q._id?.toString() ||
          item.quotationId?.toString() === q._id?.toString()
      ) && sampleCart.items.find(
        (item) =>
          item.quotationId?._id?.toString() === q._id?.toString() ||
          item.quotationId?.toString() === q._id?.toString()
      )?.isPaid;

      return !isAlreadyPaid;
    });

    if (unpaidQuotations.length === 0) {
      toast("All selected quotations have already been paid for.", { icon: "ℹ️" });
      return;
    }

    // Open modal with ONLY unpaid quotations
    setBulkProformaModalOpen(true);
    // Store unpaid quotations in a ref to pass to modal
    setUnpaidQuotationsForPayment(unpaidQuotations);
  };

  const handleConfirmSampleRequest = async () => {
    if (!sampleCart?.items?.length) return;

    const alreadyProceededIds = new Set(
      selectedQuotations.map((q) => q._id?.toString())
    );

    const quotationIds = sampleCart.items
      .filter((item) => !item.isPaid)
      .map((item) =>
        typeof item.quotationId === "object"
          ? item.quotationId._id
          : item.quotationId
      )
      .filter((id) => !alreadyProceededIds.has(id?.toString()));

    if (quotationIds.length === 0) {
      toast("All cart items have already been submitted.", { icon: "ℹ️" });
      return;
    }

    try {
      setCartProceedLoading(true);
      const { data } = await axios.post("/transport-charge/update-bulk-express", {
        quotationIds,
      });
      if (data?.success) {
        toast.success(
          data.summary?.failed > 0
            ? `Sample request submitted (${data.summary.succeeded} succeeded, ${data.summary.failed} failed)`
            : "Sample request submitted successfully!"
        );
        await fetchOrders();
        if (selectedOrder) {
          await fetchQuotations(selectedOrder._id);
          await fetchSampleCart(selectedOrder._id);
        }
        setShowGridForAddMore(false);
      } else {
        toast.error(data?.message || "Failed to submit sample request");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to submit sample request"
      );
    } finally {
      setCartProceedLoading(false);
    }
  };

  const handleProceedFromCart = async () => {
    if (!sampleCart?.items?.length) return;

    const isFirstTime =
      !quotations.some((q) => q.isSelectedByBuyer) &&
      selectedOrder?.stages === "Quotation";

    if (isFirstTime) {
      setShowSampleConfirmationModal(true);
    } else {
      // Already submitted before — proceed directly without modal
      await handleConfirmSampleRequest();
    }
  };

  const handleProceedFullOrderFromList = async (quotation) => {
    try {
      setFullOrderUpdating(true);
      setFullOrderLoadingId(quotation._id);
      const { data } = await axios.post(
        `/transport-charge/${quotation._id}/express-interest`,
        { charge_is_for: "Full_Order", isSampleRequested: false }
      );
      if (data?.success) {
        toast.success("Proceeding to full order 🚀");
        await fetchOrders();
        if (selectedOrder) await fetchQuotations(selectedOrder._id);
      } else {
        toast.error(data?.message || "Failed to proceed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setFullOrderUpdating(false);
      setFullOrderLoadingId(null);
    }
  };

  // COMPARE ACTIONS (unchanged)
  const handleCheck = (quotation) => {
    const exists = checkedQuotations.find((q) => q._id === quotation._id);
    if (exists) {
      setCheckedQuotations(
        checkedQuotations.filter((q) => q._id !== quotation._id)
      );
    } else {
      if (checkedQuotations.length >= 3) {
        toast.error("You can compare maximum 3 quotations");
        return;
      }
      setCheckedQuotations([...checkedQuotations, quotation]);
    }
  };

  const handleCompare = () => {
    if (checkedQuotations.length < 2) {
      toast.error("Please select at least 2 quotations to compare");
      return;
    }
    setShowCompareModal(true);
  };

  // ── REPLACE this function ─────────────────────────────────────────────────
  const handleSelectFromComparison = (quotation) => {
    setShowCompareModal(false);

    // If cart already has items (sample mode established), add directly — no modal
    if (sampleCart && sampleCart.items?.length > 0) {
      doAddToCart(quotation);
      return;
    }

    // If any quotation is already sample-selected (Proceed_With_Sample), 
    // add directly to cart without asking — sample mode is already decided
    const sampleModeActive = selectedQuotations.length > 0;
    if (sampleModeActive) {
      doAddToCart(quotation);
      return;
    }

    // First selection ever — ask sample or full order
    setPendingCartQuotation(quotation);
    setShowSampleModal(true);
  };

  // ORDER SELECT
  const handleOrderSelect = (order) => {
    setSelectedQuotationDetails(null);
    setCheckedQuotations([]);
    setPendingCartQuotation(null);
    setActiveSelectedQuotation(null);
    setShowGridForAddMore(false);
    setShowTransactions(false);
    navigate(`/quotations?highlight=${order._id}`, { replace: true });
    setSelectedOrder(order);
    if (window.innerWidth < 768) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleViewDetails = (quotation) => {
    setSelectedQuotationDetails(quotation);
  };

  // DERIVED VALUES
  const bestMatchId = useMemo(() => {
    if (!quotations.length) return null;
    return [...quotations].sort(
      (a, b) => (b.matchingPercentage || 0) - (a.matchingPercentage || 0)
    )[0]?._id;
  }, [quotations]);

  const getQuotationCount = (orderId) =>
    orders
      .find((o) => o._id === orderId)
      ?.responses_short_info?.reduce(
        (sum, r) => sum + (r.totalQuotationsSubmitted || 0),
        0
      ) || 0;

  const hasSelectedQuotation = (orderId) => {
    if (!orderId) return false;
    const order = orders.find((o) => o._id === orderId);
    return (
      order?.stages === "Sampling" || order?.stages === "Order-Fullfillment"
    );
  };

  const showDrillDown = !!activeSelectedQuotation;
  const showDetail = !!selectedQuotationDetails && !showDrillDown;
  const showDirectFullOrder =
    !!selectedQuotation && !showDrillDown && !showDetail;
  const showSampleListView =
    selectedQuotations.length > 0 && !showDrillDown && !showDetail && !showDirectFullOrder;
  const showGrid =
    !showDrillDown && !showDetail && !showDirectFullOrder && !showSampleListView;

  // SHARED QUOTATIONS GRID
  const QuotationsGrid = ({ isMobile = false }) => (
    <div className="pb-11 md:pb-0">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">
          {quotations.length} quotation{quotations.length !== 1 ? "s" : ""}
          {checkedQuotations.length > 0 && (
            <span className="ml-2 text-primaryColor font-semibold">
              · {checkedQuotations.length} selected for compare
            </span>
          )}
          {cartQuotationIds.size > 0 && (
            <span className="ml-2 text-primaryColor font-semibold">
              · {cartQuotationIds.size} in cart
            </span>
          )}
        </p>
        {/* hint text only — actual compare is the floating button */}
        {checkedQuotations.length === 1 && (
          <span className="text-xs text-gray-400">Select 1 more to compare</span>
        )}
      </div>

      <div
        className={`grid gap-3 ${isMobile
          ? "grid-cols-2"
          : "grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          }`}
      >
        {quotations.map((quotation) => {
          const isInCart = cartQuotationIds.has(quotation._id?.toString());
          const isSampleSelected =
            quotation.buyerDecision === "Proceed_With_Sample";
          const isPaidInCart = isInCart && sampleCart?.items?.find(
            (item) =>
              (item.quotationId?._id?.toString() === quotation._id?.toString() ||
                item.quotationId?.toString() === quotation._id?.toString()) &&
              item.isPaid
          );
          const isFullOrderSelected =
            quotation.isSelectedByBuyer &&
            quotation.buyerDecision === "Proceed_Full_Order" &&
            !quotation.sampleDetails?.requested;

          return (
            <QuotationCard
              key={quotation._id}
              quotation={quotation}
              order={selectedOrder}
              onViewDetails={() => handleViewDetails(quotation)}
              isChecked={checkedQuotations.some((q) => q._id === quotation._id)}
              onCheck={() => handleCheck(quotation)}
              onAddToCart={handleAddToCart}
              onRemoveFromCart={handleRemoveFromCart}
              isBestMatch={quotation._id === bestMatchId}
              isSelected={quotation.isSelectedByBuyer}
              isInCart={isInCart}
              isSampleSelected={isSampleSelected}
              isFullOrderSelected={isFullOrderSelected}
              cartLoading={cardCartLoadingId === quotation._id}
              isPaidInCart={isPaidInCart}
            />
          );
        })}
      </div>

      {/* ── Cart Bar ────────────────────────────────────────────────────── */}
      <SampleCartBar
        cartCount={sampleCart?.items?.length || 0}
        totalAmount={sampleCart?.finalCartAmount || 0}
        onProceed={handleProceedFromCart}
        loading={cartProceedLoading}
        sampleCart={sampleCart}
      />
    </div>
  );

  // RIGHT PANEL CONTENT (used in both mobile and desktop)
  const RightPanelContent = ({ isMobile = false }) => {
    // 1. Drill-down into a single selected quotation
    if (showDrillDown) {
      return (
        <>
          <button
            onClick={() => setActiveSelectedQuotation(null)}
            className="flex items-center gap-2 text-primaryColor mb-4 font-medium"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Quotations
          </button>
          {showDrillDown && (
            <SelectedQuotationView
              quotation={activeSelectedQuotation}
              order={selectedOrder}
              refreshQuotations={() => fetchQuotations(selectedOrder._id)}
              refreshOrders={fetchOrders}
            />
          )}
        </>
      );
    }

    // 2. Quotation detail view
    if (showDetail) {
      return (
        <>
          <button
            onClick={() => setSelectedQuotationDetails(null)}
            className="mb-4 text-primaryColor font-medium flex items-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to Quotations
          </button>
          <QuotationSummaryCard
            q={selectedQuotationDetails}
            order={selectedOrder}
            onAddToCart={handleAddToCart}
            onRemoveFromCart={handleRemoveFromCart}
            sampleCart={sampleCart}
            cartQuotationIds={cartQuotationIds}
            cartLoading={cardCartLoadingId === selectedQuotationDetails?._id}
            isSampleSelected={selectedQuotationDetails?.buyerDecision === "Proceed_With_Sample"}
            isFullOrderSelected={
              selectedQuotationDetails?.isSelectedByBuyer &&
              selectedQuotationDetails?.buyerDecision === "Proceed_Full_Order" &&
              !selectedQuotationDetails?.sampleDetails?.requested
            }
            isPaidInCart={
              sampleCart?.items?.find(
                (item) =>
                  (item.quotationId?._id?.toString() === selectedQuotationDetails?._id?.toString() ||
                    item.quotationId?.toString() === selectedQuotationDetails?._id?.toString()) &&
                  item.isPaid
              )
            }
          />
          <QuotationDetailsContent q={selectedQuotationDetails} />
        </>
      );
    }

    // 3. Direct full order selected (single, no sample) — legacy unchanged
    if (showDirectFullOrder) {
      return (
        <div className="space-y-4">
          {/* ── Toggle bar: show sample quotations if any exist ── */}
          {selectedQuotations.length > 0 && (
            <div className="flex items-center justify-between bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-primaryColor/10 flex items-center justify-center flex-shrink-0">
                  <FiPackage className="text-primaryColor" size={14} />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">
                    {selectedQuotations.length} Sample Quotation{selectedQuotations.length !== 1 ? "s" : ""}
                  </p>
                  <p className="text-xs text-gray-500">
                    {selectedQuotations.filter(q =>
                      sampleCart?.items?.find(i =>
                        (i.quotationId?._id?.toString() === q._id?.toString() ||
                          i.quotationId?.toString() === q._id?.toString()) && i.isPaid
                      )
                    ).length} paid · tap to manage
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowSampleListAlongside(prev => !prev)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${showSampleListAlongside
                  ? "bg-primaryColor text-white border-primaryColor"
                  : "bg-primaryBtnColor text-white border-white hover:bg-primaryBtnHoverColor"
                  }`}
              >
                {showSampleListAlongside ? "Hide Sample Quotations" : "View Sample Quotations"}
              </button>
            </div>
          )}

          {/* ── Collapsible sample list ── */}
          {showSampleListAlongside && selectedQuotations.length > 0 && (
            <div className="border border-dashed border-primaryColor/20 rounded-2xl overflow-hidden">
              <div className="bg-primaryColor/3 px-4 py-2 border-b border-primaryColor/10">
                <p className="text-[10px] font-black text-primaryColor uppercase tracking-widest">
                  Sample Quotations — Manage & Track
                </p>
              </div>
              <div className="p-3">
                <SelectedQuotationsListView
                  quotations={selectedQuotations}
                  order={selectedOrder}
                  onViewManage={(q) => setActiveSelectedQuotation(q)}
                  onAddMore={() => {
                    setShowGridForAddMore(prev => !prev);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  onProceedFullOrder={handleProceedFullOrderFromList}
                  onProceedSamplePayment={handleProceedSamplePayment}
                  updating={fullOrderUpdating}
                  fullOrderLoadingId={fullOrderLoadingId}
                  sampleCart={sampleCart}
                  hideAddMore={true}
                  refreshQuotations={() => fetchQuotations(selectedOrder._id)}
                />
              </div>
            </div>
          )}

          {/* ── Full order view ── */}
          {showDirectFullOrder && (
            <SelectedQuotationView
              quotation={selectedQuotation}
              order={selectedOrder}
              refreshQuotations={() => fetchQuotations(selectedOrder._id)}
              refreshOrders={fetchOrders}
            />
          )}
        </div>
      );
    }

    if (showSampleListView) {
      return (
        <div className="space-y-6">
          {/* ── "Add More" grid — shown ABOVE the list when active ──────── */}
          {showGridForAddMore && (
            <div>
              <div className="flex items-center justify-between mb-4 bg-gray-50 border border-dashed border-gray-300 rounded-lg px-4 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-black text-gray-500 uppercase tracking-widest">
                    Add More Quotations
                  </span>
                </div>
                <button
                  onClick={() => setShowGridForAddMore(false)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-600 text-xs font-semibold hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors shadow-sm"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Close
                </button>
              </div>

              {loadingQuotations ? (
                <div className="h-48 flex items-center justify-center">
                  <AppLoader fullScreen={false} showLogo />
                </div>
              ) : quotations.length > 0 ? (
                <QuotationsGrid isMobile={isMobile} />
              ) : (
                <EmptyQuotationState />
              )}

              {/* Divider between grid and list */}
              <div className="flex items-center gap-3 mt-6">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">
                  Selected Quotations
                </span>
                <div className="flex-1 border-t border-gray-200" />
              </div>
            </div>
          )}

          {/* ── List of sample-selected quotations ──────────────────────── */}
          <SelectedQuotationsListView
            quotations={selectedQuotations}
            order={selectedOrder}
            onViewManage={(q) => setActiveSelectedQuotation(q)}
            onAddMore={() => {
              setShowGridForAddMore((prev) => !prev);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            onProceedFullOrder={handleProceedFullOrderFromList}
            onProceedSamplePayment={handleProceedSamplePayment}
            updating={fullOrderUpdating}
            fullOrderLoadingId={fullOrderLoadingId}
            sampleCart={sampleCart}
            hideAddMore={showGridForAddMore}
            refreshQuotations={() => fetchQuotations(selectedOrder._id)}
          />
        </div>
      );
    }

    // 5. Default grid view (with cart bar if items in cart)
    if (loadingQuotations) {
      return (
        <div className="h-[50vh] flex items-center justify-center">
          <AppLoader fullScreen={false} showLogo={true} />
        </div>
      );
    }
    if (quotations.length > 0) {
      return <QuotationsGrid isMobile={isMobile} />;
    }
    return <EmptyQuotationState />;
  };

  // RENDER
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="2xl:max-w-7xl mx-auto px-4 md:px-8 py-4 pb-6 md:pb-0 sm:py-6 mb-16 md:mb-0">
        {/* Header */}
        <div
          className={`mb-4 sm:mb-5 ${selectedOrder && window.innerWidth < 768 ? "hidden" : "block"
            }`}
        >
          <h1 className="text-xl flex items-center gap-2 sm:text-2xl font-bold text-primaryColor mb-1">
            <FiShoppingBag /> Orders with Quotations
          </h1>
          <p className="text-gray-500 text-sm">
            View and manage your orders that have received quotations. Tap on an order to see details, compare quotations, and proceed with samples or full orders.
          </p>
        </div>

        {loading && (
          <div className="h-[50vh] flex items-center justify-center">
            <AppLoader
              fullScreen={false}
              message="Loading your orders..."
              showLogo
            />
          </div>
        )}
        {!loading && error && <ErrorState onRetry={fetchOrders} />}
        {!loading && !error && orders.length === 0 && <EmptyOrders />}

        {!loading && !error && orders.length > 0 && (
          <>
            {/* ── Mobile ─────────────────────────────────────────────────── */}
            <div className="md:hidden">
              {!selectedOrder ? (
                <>
                  <div className="space-y-3 mb-3">
                    {orders.map((order) => (
                      <OrderListItem
                        key={order._id}
                        order={order}
                        isActive={selectedOrder?._id === order._id}
                        quotationCount={order?.approvedQuotationCount || 0}
                        hasSelectedQuotation={hasSelectedQuotation(order._id)}
                        onClick={() => handleOrderSelect(order)}
                      />
                    ))}
                  </div>
                  {pagination && (
                    <Pagination
                      pagination={pagination}
                      page={page}
                      setPage={setPage}
                      limit={limit}
                      setLimit={setLimit}
                    />
                  )}
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setSelectedOrder(null);
                      setSelectedQuotationDetails(null);
                      navigate("/quotations", { replace: true });
                    }}
                    className="flex items-center gap-2 text-primaryColor mb-4 font-medium"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                    Back to Orders
                  </button>

                  {/* Order header card */}
                  <div className="bg-gradient-to-br shineEffect from-[#772037] to-[#CC234F] rounded-2xl mb-3 p-4 sm:p-5 text-white shadow-lg shadow-[#772037]/20">
                    <div className="space-y-4">
                      {/* Order Number - Full Width */}
                      <div>
                        <h2 className="font-bold text-lg sm:text-xl text-white">
                          Order #{selectedOrder.orderNumber}
                        </h2>
                      </div>

                      {/* Content Row - Responsive (stack on mobile, side-by-side on larger screens) */}
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-4">

                        {/* Left Side */}
                        <div className="text-xs sm:text-sm text-gray-300 space-y-2 flex-1">
                          <p>
                            <span className="font-medium">Product:</span>{" "}
                            <span className="block sm:inline">{selectedOrder.productVariety?.varietyName}</span>
                          </p>

                          <p>
                            <span className="font-medium">Order quantity:</span>{" "}
                            <span className="block sm:inline">
                              {selectedOrder.priceAndQuantity?.MOQ}{" "}
                              {selectedOrder.priceAndQuantity?.standardizedUnit}
                            </span>
                          </p>

                          <p>
                            <span className="font-medium">
                              {selectedOrder.priceAndQuantity?.expectedPriceRange?.min === 0 &&
                                selectedOrder.priceAndQuantity?.expectedPriceRange?.max === 0
                                ? "Fixed Price:"
                                : "Price Range:"}
                            </span>{" "}
                            <span className="block sm:inline">
                              {selectedOrder.priceAndQuantity?.currency}{" "}
                              {selectedOrder.priceAndQuantity?.expectedPriceRange?.min === 0 &&
                                selectedOrder.priceAndQuantity?.expectedPriceRange?.max === 0
                                ? selectedOrder.priceAndQuantity?.fixedPrice?.price
                                : `${selectedOrder.priceAndQuantity?.expectedPriceRange?.min} – ${selectedOrder.priceAndQuantity?.expectedPriceRange?.max}`}
                            </span>
                          </p>
                        </div>

                        {/* Right Side */}
                        <div className="flex flex-col sm:items-end gap-2 sm:flex-shrink-0">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-medium w-fit ${selectedOrder.stages === "Completed"
                              ? "bg-green-100 text-green-700"
                              : selectedOrder.stages === "Sampling"
                                ? "bg-purple-100 text-purple-700"
                                : selectedOrder.stages === "Quotation"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-orange-100 text-orange-700"
                              }`}
                          >
                            {selectedOrder.stages}
                          </span>

                          <div className="flex flex-col gap-1.5 w-full sm:w-auto">
                            <button
                              onClick={() => setShowTransactions(!showTransactions)}
                              className="flex items-center justify-center sm:justify-start gap-1.5 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs font-semibold transition backdrop-blur-sm border border-white/30"
                            >
                              <span>📜</span>
                              <span className="hidden sm:inline">
                                {showTransactions ? "Quotations" : "View Transactions History"}
                              </span>
                              <span className="sm:hidden">
                                {showTransactions ? "Click to view quotations" : "Click to view transactions"}
                              </span>
                            </button>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>

                  {showTransactions ? (
                    <div className="bg-white rounded-2xl border border-gray-200 p-2 sm:p-5 shadow-sm">
                      <TransactionsHistoryTab orderId={selectedOrder._id} />
                    </div>
                  ) : (
                    <>
                      {loadingQuotations && !showDrillDown && !showSampleListView ? (
                        <div className="h-[50vh] flex items-center justify-center">
                          <AppLoader fullScreen={false} showLogo={true} />
                        </div>
                      ) : (
                        <RightPanelContent isMobile />
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {/* ── Desktop ─────────────────────────────────────────────────── */}
            <div className="hidden md:block">
              <div className="grid md:grid-cols-12 gap-5">
                {/* Left: order list */}
                <div className="md:col-span-4 lg:col-span-3 space-y-3 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto">
                  {orders.map((order) => (
                    <OrderListItem
                      key={order._id}
                      order={order}
                      isActive={selectedOrder?._id === order._id}
                      quotationCount={order?.approvedQuotationCount || 0}
                      hasSelectedQuotation={hasSelectedQuotation(order._id)}
                      onClick={() => handleOrderSelect(order)}
                    />
                  ))}

                  {pagination && pagination.totalPages > 1 && (
                    <div className="pt-3 border-t border-gray-200">
                      <Pagination
                        pagination={pagination}
                        page={page}
                        setPage={setPage}
                        limit={limit}
                        setLimit={setLimit}
                      />
                    </div>
                  )}
                </div>

                {/* Right: content */}
                <div className="md:col-span-8 lg:col-span-9">
                  {selectedOrder && (
                    <>
                      {/* Order header card */}
                      <div className="bg-gradient-to-br shineEffect from-[#772037] to-[#CC234F] rounded-2xl p-4 sm:p-5 text-white shadow-lg shadow-[#772037]/20 mb-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h2 className="font-bold text-xl text-white mb-2">
                              Order Number: {selectedOrder.orderNumber}
                            </h2>
                            <div className="text-sm text-gray-300 space-y-1">
                              <p>
                                <span className="font-medium">Product:</span>{" "}
                                {selectedOrder.productVariety?.varietyName}
                              </p>
                              <p>
                                <span className="font-medium">
                                  Order Quantity:
                                </span>{" "}
                                {selectedOrder.priceAndQuantity?.MOQ}{" "}
                                {selectedOrder.priceAndQuantity?.standardizedUnit}
                              </p>
                              <p>
                                <span className="font-medium">
                                  {selectedOrder.priceAndQuantity?.expectedPriceRange?.min === 0 &&
                                    selectedOrder.priceAndQuantity?.expectedPriceRange?.max === 0
                                    ? "Fixed Price:"
                                    : "Price Range:"}
                                </span>{" "}
                                {selectedOrder.priceAndQuantity?.currency}{" "}
                                {selectedOrder.priceAndQuantity?.expectedPriceRange?.min === 0 &&
                                  selectedOrder.priceAndQuantity?.expectedPriceRange?.max === 0
                                  ? selectedOrder.priceAndQuantity?.fixedPrice?.price
                                  : `${selectedOrder.priceAndQuantity?.expectedPriceRange?.min} – ${selectedOrder.priceAndQuantity?.expectedPriceRange?.max}`}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-end flex-col justify-between mb-2 gap-2">
                            <span
                              className={`px-3 py-1 rounded-full text-xs font-medium ${selectedOrder.stages === "Completed"
                                ? "bg-green-100 text-green-700"
                                : selectedOrder.stages === "Sampling"
                                  ? "bg-purple-100 text-purple-700"
                                  : selectedOrder.stages === "Quotation"
                                    ? "bg-blue-100 text-blue-700"
                                    : "bg-orange-100 text-orange-700"
                                }`}
                            >
                              {selectedOrder.stages}
                            </span>
                            <div className="flex flex-col gap-1.5">
                              <button
                                onClick={() => setShowTransactions(!showTransactions)}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white text-xs font-semibold transition backdrop-blur-sm border border-white/30 whitespace-nowrap"
                              >
                                <span>📜</span>
                                {showTransactions ? "Click to view quotations" : " Click to view transactions"}
                              </button>

                              {/* {selectedQuotations.length > 0 && showDirectFullOrder && (
                                <button
                                  onClick={() => {
                                    setShowSampleListAlongside(prev => !prev);
                                    setShowTransactions(false);
                                  }}
                                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition border whitespace-nowrap ${showSampleListAlongside
                                    ? "bg-white text-primaryColor border-white"
                                    : "bg-white/20 hover:bg-white/30 text-white border-white/30 backdrop-blur-sm"
                                    }`}
                                >
                                  <FiPackage size={12} />
                                  {showSampleListAlongside ? "Hide Samples" : `My Sample Quotations (${selectedQuotations.length})`}
                                </button>
                              )} */}
                            </div>
                          </div>
                        </div>
                      </div>

                      {showTransactions ? (
                        <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-5 shadow-sm">
                          <TransactionsHistoryTab orderId={selectedOrder._id} />
                        </div>
                      ) : (
                        <>
                          {loadingQuotations && !showDrillDown && !showSampleListView ? (
                            <div className="h-[50vh] flex items-center justify-center">
                              <AppLoader fullScreen={false} showLogo />
                            </div>
                          ) : (
                            <RightPanelContent />
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── Modals ───────────────────────────────────────────────────────── */}
        {showCompareModal && (
          <ComparisonModal
            quotations={checkedQuotations}
            onClose={() => setShowCompareModal(false)}
            onSelectQuotation={handleSelectFromComparison}
            sampleCart={sampleCart}
            selectedQuotations={selectedQuotations}
            cartQuotationIds={cartQuotationIds}
          />
        )}

        {showSampleModal && pendingCartQuotation && (
          <SampleRequestModal
            quotation={pendingCartQuotation}
            order={selectedOrder}
            onClose={() => {
              setShowSampleModal(false);
              setPendingCartQuotation(null);
            }}
            onConfirm={handleSampleModalConfirm}
          />
        )}

        <SampleRequestConfirmationModal
          open={showSampleConfirmationModal}
          onClose={() => setShowSampleConfirmationModal(false)}
          onProceedSample={handleConfirmSampleRequest}
          onAddMore={handleAddMoreFromConfirmation}
          onSwitchToFullOrder={handleSwitchToFullOrderFromConfirmation}
          cartItems={sampleCart?.items || []}
        />

        {/* ── NEW: Bulk Proforma Invoice Modal ────────────────────────────── */}
        {bulkProformaModalOpen && (
          <BulkProformaInvoiceModal
            open={bulkProformaModalOpen}
            onClose={() => setBulkProformaModalOpen(false)}
            quotations={unpaidQuotationsForPayment}
            sampleCart={sampleCart}
            order={selectedOrder}
            user={user}
            onSuccess={async () => {
              toast.success("Sample orders confirmed! 🎉");
              await fetchOrders();
              if (selectedOrder) {
                await fetchQuotations(selectedOrder._id);
                await fetchSampleCart(selectedOrder._id);
              }
            }}
          />
        )}

        {/* ── Mobile: floating compare button ─────────────────────────────── */}
        {checkedQuotations.length >= 2 && (
          <div className="fixed bottom-20 md:bottom-8 right-4 md:right-8 z-50">
            <button
              onClick={handleCompare}
              className="flex items-center gap-2 px-5 py-3.5 bg-primaryColor text-white rounded-2xl shadow-2xl shadow-primaryColor/30 hover:bg-primaryBtnHoverColor transition-all hover:scale-105 active:scale-95 font-bold text-sm"
            >
              <MdCompare className="text-lg" />
              Compare ({checkedQuotations.length})
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Quotations;